import { DataGridProps } from '@mui/x-data-grid';
import { Stack, SvgIcon } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { GridSortItem } from '@mui/x-data-grid/models/gridSortModel';
import { QueryKey } from '@tanstack/react-query';

import { SortedTable } from 'types/apiParamsTypes';
import { colors } from 'theme/mui/colors';
import { useLocalization } from 'localization';
import { DataGridTableStyled } from './DataGridTableStyled';
import FetchingErrorAlert from '../alert/FetchingErrorAlert';

export interface Props extends DataGridProps {
  loading?: boolean;
  hasNextPage?: boolean;
  handleSortChange?: (props: SortedTable) => void;
  scrollThresholdPx?: number;
  initialSortModel?: GridSortItem[];
  rowCount?: number;
  page?: number;
  pageSize?: number;
  isHideFooter?: boolean;
  onPagination?: (pageNum: number, pageSize: number) => void;
  emptyText?: string;
  isError?: boolean;
  refetch?: () => void;
  queryKey?: readonly QueryKey[];
}

const DataGridTable = (props: Props) => {
  const {
    rows,
    columns,
    handleSortChange,
    loading = false,
    initialSortModel = [],
    rowCount = 0,
    page = 0,
    pageSize = 10,
    onPagination,
    isHideFooter,
    emptyText,
    hasNextPage,
    scrollThresholdPx,
    isError = false,
    refetch,
    queryKey,
    ...dataGridProps
  } = props;

  const { t } = useLocalization();

  const AscIcon = (p: any) => <SvgIcon {...p} component={ArrowUpward} sx={{ color: colors.primary.main }} />;
  const DescIcon = (p: any) => <SvgIcon {...p} component={ArrowDownward} sx={{ color: colors.primary.main }} />;

  return (
    <DataGridTableStyled
      rows={rows}
      columns={columns}
      loading={loading}
      initialState={{
        sorting: {
          sortModel: initialSortModel,
        },
        pagination: {
          paginationModel: {
            pageSize: pageSize,
          },
        },
      }}
      sortingOrder={['asc', 'desc']}
      sortingMode="server"
      onSortModelChange={model => {
        handleSortChange?.({
          sortOrder: model[0]?.field ?? '',
          sortDirection: model[0]?.sort ?? '',
        });
      }}
      //PAGINATION
      pagination
      paginationMode="server"
      rowCount={rowCount}
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={paginationModel => {
        onPagination?.(paginationModel.page, paginationModel.pageSize);
      }}
      pageSizeOptions={[10, 25, 50]}
      disableRowSelectionOnClick
      hideFooter={isHideFooter}
      disableColumnMenu
      slots={{
        columnSortedAscendingIcon: AscIcon,
        columnSortedDescendingIcon: DescIcon,
        noRowsOverlay: isError
          ? () => (
              <Stack height={'100%'} justifyContent="center" alignItems="center">
                <FetchingErrorAlert onRetry={refetch} isLoading={loading} queryKey={queryKey} />
              </Stack>
            )
          : undefined,
      }}
      rowHeight={65}
      localeText={{
        noRowsLabel: emptyText ?? t('common.terms.noData'),
      }}
      {...dataGridProps}
    />
  );
};

export default DataGridTable;
